import document from 'global/document';
import videojs from 'video.js';

import Tile from './Tile';
import OverlayBackButton from './OverlayBackButton';
import OverlayForwardButton from './OverlayForwardButton';

const ModalDialog = videojs.getComponent('ModalDialog');

// set as a constant for now, but will be refactored later
const TILES_PER_PAGE = 4;

/**
 * A video.js Component for the 'next item' button
 * @param {Object} player A video.js player instance.
 * @param {Object} options A set of options passed to the component.
 */
export default class Overlay extends ModalDialog {
  constructor(player, options) {
    const opts = {
      ...options,

      // We want to make sure to override this so we don't accidentally
      // dispose the modal when we close it.
      temporary: false,
    };

    super(player, opts);

    this.addClass('vjs-playlist-overlay');

    // storing this so we can modify if needed on the fly
    this.tilesPerPage = TILES_PER_PAGE;
    this.currentIndex = 0;
    this.videos = options.videos;

    this.setHandlers(player);
  }

  /**
   * Per some discussion with @al, we're listening on child components for
   * notifications from parent components to make things scalabale and not require
   * the main playlist page to handle the plumbing of these events.
   * @param {videojs} player A video.js player instance.
   */
  setHandlers(player) {
    player.on('controls:overlay:open', () => this.open());
    player.on('controls:overlay:close', () => this.close());
    player.on('controls:overlay:forward', () => this.nextPage());
    player.on('controls:overlay:back', () => this.previousPage());
  }

  /**
   * Builds the content of the Overlay modal. This is handled as a separate method because of
   * the way that DialogModal generates its internals and handles them on open/close.
   * @return {HTMLElement}
   */
  content() {
    const el = document.createElement('div');
    el.className = 'vjs-overlay-contents';

    // keep the tiles element around so we can re-render it later
    this.tilesEl = this.getTiles(this.player_, this.options_.videos);
    el.appendChild(this.tilesEl);

    // glue down the overlay forward/back buttons. If these are appended in the other order
    // the end up flipped
    el.appendChild(new OverlayForwardButton(this.player_, this.options_).el());
    el.appendChild(new OverlayBackButton(this.player_, this.options_).el());

    return el;
  }

  /**
   * Returns a rendered set of video tiles, starting from the index provided.
   * @param {Object} player A videojs player instance.
   * @param {[Object]} videos A collection of objects containing video information.
   * @param {index} startIndex the index of the collection to render tiles from.
   */
  getTiles(player, videos, startIndex = 0) {
    const el = document.createElement('div');
    el.className = 'vjs-tiles';

    // only ever go 6 tiles forward
    for (let i = startIndex; i < videos.length && i < startIndex + this.tilesPerPage; i++) {
      const options = videos[i];
      options.index = i;

      el.appendChild(new Tile(player, options).el());
    }

    return el;
  }

  /**
   * Pages the tiles forward and re-renders if necessary.
   * @return {void}
   */
  nextPage() {
    if (this.currentIndex + this.tilesPerPage < this.videos.length) {
      this.currentIndex += this.tilesPerPage;

      this.renderTiles();
    }
  }

  /**
   * Pages the tiles forward and re-renders if necessary.
   * @return {void}
   */
  previousPage() {
    if (this.currentIndex - this.tilesPerPage >= 0) {
      this.currentIndex -= this.tilesPerPage;

      this.renderTiles();
    }
  }

  /**
   * Re-renders the video tiles element by replacing the root DOM element.
   * @return {void}
   */
  renderTiles() {
    const oldTiles = this.tilesEl;
    const newTiles = this.getTiles(this.player_, this.videos, this.currentIndex);
    const parentNode = this.el_.querySelector('.vjs-overlay-contents');

    parentNode.replaceChild(newTiles, oldTiles);
    this.tilesEl = newTiles;
  }
}
